import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Banner, Spinner } from '../components/States';
import { CredentialsPanel } from '../components/CredentialsPanel';
import { ColorsPanel } from '../components/ColorsPanel';
import { KindIconsPanel } from '../components/KindIconsPanel';
import { FeaturedPanel } from '../components/FeaturedPanel';
import { KitsPanel } from '../components/KitsPanel';
import { OrdersPanel } from '../components/OrdersPanel';
import { FaqPanel } from '../components/FaqPanel';
import { FeedbackPanel } from '../components/FeedbackPanel';
import { EquipePanel, PERFIS } from '../components/EquipePanel';
import { CuponsPanel } from '../components/CuponsPanel';
import { useCurrency } from '../hooks/useCurrency';
import { dateTime } from '../lib/format';
import type {
  AdminClient,
  AdminClientsResponse,
  PricingPreviewResponse,
  PricingTiersResponse,
  SyncProgress,
  TierInput,
} from '../api/types';

type Aba =
  | 'pedidos'
  | 'duvidas'
  | 'feedbacks'
  | 'destaques'
  | 'kits'
  | 'comissionamento'
  | 'clientes'
  | 'cores'
  | 'icones'
  | 'catalogo'
  | 'credenciais'
  | 'cupons'
  | 'usuarios';

const ABAS: { key: Aba; label: string }[] = [
  { key: 'pedidos', label: 'Pedidos' },
  { key: 'duvidas', label: 'Dúvidas' },
  { key: 'feedbacks', label: 'Feedbacks' },
  { key: 'destaques', label: 'Destaques' },
  { key: 'kits', label: 'Kits' },
  { key: 'comissionamento', label: 'Comissionamento' },
  { key: 'cupons', label: 'Cupons' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'cores', label: 'Cores' },
  { key: 'icones', label: 'Ícones' },
  { key: 'catalogo', label: 'Catálogo' },
  { key: 'credenciais', label: 'Credenciais' },
  { key: 'usuarios', label: 'Usuários' },
];

export function AdminPage() {
  const { user, loading, isAdmin, loginUrl, papel, somenteLeitura } = useAuth();
  const [recusado, setRecusado] = useState(false);

  // A API recusou uma alteração do perfil Desenvolvedor: o aviso some sozinho.
  useEffect(() => {
    let t = 0;
    const avisar = () => {
      setRecusado(true);
      window.clearTimeout(t);
      t = window.setTimeout(() => setRecusado(false), 4000);
    };
    window.addEventListener('dms:somente-leitura', avisar);
    return () => {
      window.removeEventListener('dms:somente-leitura', avisar);
      window.clearTimeout(t);
    };
  }, []);
  const [params, setParams] = useSearchParams();
  // A aba vem na URL: "Gestão de pedidos" no menu abre direto na fila, e o
  // link do painel continua compartilhável.
  const abaUrl = params.get('aba') as Aba | null;
  const aba: Aba = ABAS.some((a) => a.key === abaUrl) ? abaUrl! : 'pedidos';
  const setAba = (proxima: Aba) => setParams({ aba: proxima }, { replace: true });

  if (loading) return <Spinner label="Verificando o seu acesso…" />;

  if (!user) {
    return (
      <div className="panel narrow">
        <h2>Área administrativa</h2>
        <p className="muted">Entre com a conta Steam que está na lista de administradores.</p>
        <a className="btn primary" href={loginUrl}>
          Entrar com a Steam
        </a>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="panel narrow">
        <h2>Área administrativa</h2>
        <Banner kind="error">
          A conta <b>{user.personaName ?? user.steamId}</b> não tem acesso. Peça ao Master ou a um Admin para
          adicionar você em <b>Usuários</b>, informando o SteamID <code>{user.steamId}</code>.
        </Banner>
      </div>
    );
  }

  return (
    <>
      <div className={`admin-perfil${somenteLeitura ? ' leitura' : ''}`}>
        <span className={`eq-selo eq-${papel}`}>{PERFIS[papel].nome}</span>
        <span>
          {somenteLeitura
            ? 'Modo visualização: você vê todo o painel, mas nenhuma alteração é salva.'
            : PERFIS[papel].resumo}
        </span>
      </div>
      {recusado && (
        <div className="admin-recusa" role="alert">
          Alteração não salva: o perfil Desenvolvedor é só de visualização.
        </div>
      )}
      <div className="admin-tabs">
        {ABAS.map((a) => (
          <button key={a.key} className={`tab ${aba === a.key ? 'active' : ''}`} onClick={() => setAba(a.key)}>
            {a.label}
          </button>
        ))}
      </div>
      {aba === 'pedidos' && <OrdersPanel />}
      {aba === 'duvidas' && <FaqPanel />}
      {aba === 'feedbacks' && <FeedbackPanel />}
      {aba === 'destaques' && <FeaturedPanel />}
      {aba === 'kits' && <KitsPanel />}
      {aba === 'comissionamento' && <PainelComissionamento />}
      {aba === 'clientes' && <PainelClientes />}
      {aba === 'cores' && <ColorsPanel />}
      {aba === 'icones' && <KindIconsPanel />}
      {aba === 'catalogo' && <PainelCatalogo />}
      {aba === 'credenciais' && <CredentialsPanel />}
      {aba === 'cupons' && <CuponsPanel />}
      {aba === 'usuarios' && <EquipePanel />}
    </>
  );
}

// ─── Comissionamento ─────────────────────────────────────────────────────────

const brl = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Enquanto edita, cada campo é texto — não número.
 *
 * Com value={number}, apagar o campo para trocar "50" por "51" vira 0 no meio
 * da digitação e o cursor pula. A conversão acontece só no salvar.
 */
interface TierDraft {
  minBrl: string;
  maxBrl: string;
  rateBonus: string;
  active: boolean;
}

const paraDraft = (t: { minBrl: number; maxBrl: number | null; rateBonus: number; active?: boolean }): TierDraft => ({
  minBrl: String(t.minBrl),
  maxBrl: t.maxBrl === null ? '' : String(t.maxBrl),
  // Vírgula é como se digita centavo em português; convertemos ao salvar.
  rateBonus: String(t.rateBonus).replace('.', ','),
  active: t.active ?? true,
});

/** Aceita "0,04" e "0.04" — ninguém deveria ter de lembrar qual o campo quer. */
const numero = (v: string) => Number(v.replace(',', '.'));

function PainelComissionamento() {
  const [data, setData] = useState<PricingTiersResponse | null>(null);
  const [rascunho, setRascunho] = useState<TierDraft[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [taxa, setTaxa] = useState('0');
  const [cotacaoKit, setCotacaoKit] = useState('0');
  const [preview, setPreview] = useState<PricingPreviewResponse | null>(null);

  const carregar = useCallback(async () => {
    try {
      const res = await api.adminTiers();
      setData(res);
      setRascunho(res.tiers.map(paraDraft));
      setTaxa(String(res.feePct));
      setCotacaoKit(String(res.kitRateBonus));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar a tabela.');
    }
  }, []);

  useEffect(() => {
    void carregar();
    void api.adminPricingPreview({ limit: 15 }).then(setPreview).catch(() => setPreview(null));
  }, [carregar]);

  function editar(i: number, campo: 'minBrl' | 'maxBrl' | 'rateBonus', valor: string) {
    setRascunho((prev) => prev.map((t, idx) => (idx === i ? { ...t, [campo]: valor } : t)));
  }

  function adicionar() {
    setRascunho((prev) => {
      const ultima = prev[prev.length - 1];
      // A faixa nova começa onde a anterior termina: encostadas, sem vão — que
      // é justamente o que a API recusa.
      const inicio = ultima ? (ultima.maxBrl.trim() === '' ? numero(ultima.minBrl) + 100 : numero(ultima.maxBrl)) : 0;
      return [...prev, { minBrl: String(inicio), maxBrl: '', rateBonus: ultima?.rateBonus ?? '0,04', active: true }];
    });
  }

  function remover(i: number) {
    setRascunho((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      const tiers: TierInput[] = rascunho.map((t) => ({
        minBrl: numero(t.minBrl || '0'),
        maxBrl: t.maxBrl.trim() === '' ? null : numero(t.maxBrl),
        rateBonus: numero(t.rateBonus || '0'),
        active: t.active,
      }));
      if (
        tiers.some(
          (t) => Number.isNaN(t.minBrl) || Number.isNaN(t.rateBonus) || (t.maxBrl !== null && Number.isNaN(t.maxBrl)),
        )
      ) {
        throw new Error('Há campo com valor que não é número.');
      }
      const feePct = numero(taxa || '0');
      if (Number.isNaN(feePct)) throw new Error('A taxa do marketplace não é um número.');
      const kitBonus = numero(cotacaoKit || '0');
      if (Number.isNaN(kitBonus)) throw new Error('A cotação de kit não é um número.');
      await api.adminSaveTiers(tiers, feePct, kitBonus);
      setOk('Tabela salva. Os preços do site já saem com a nova margem.');
      await carregar();
      setPreview(await api.adminPricingPreview({ limit: 15 }));
    } catch (e) {
      setErro(e instanceof ApiError || e instanceof Error ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  if (!data) return erro ? <Banner kind="error">{erro}</Banner> : <Spinner label="Carregando a tabela…" />;

  return (
    <div className="panel">
      <h2>Tabela de comissionamento</h2>
      <p className="muted">
        <b>(preço ¥ × taxa do marketplace) × (cotação da Wise + acréscimo da faixa)</b>. A cotação de hoje é{' '}
        {data.cnyToBrl ? <b>{data.cnyToBrl.toFixed(4)}</b> : <i>indisponível</i>}; o acréscimo é o seu lucro em
        reais por yuan, e a faixa é escolhida pelo custo já convertido.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <label className="field fee-field">
        <span>Taxa do marketplace (%)</span>
        <input inputMode="decimal" value={taxa} onChange={(e) => setTaxa(e.target.value)} />
        <small>
          O que o marketplace cobra sobre o preço de tabela, aplicado <b>antes</b> da conversão. No seu exemplo,
          o ×1,01 equivale a <b>1</b> aqui. É custo, não lucro, e por isso fica separado do acréscimo.
        </small>
      </label>

      <label className="field fee-field">
        <span>Acréscimo de kit (R$ por ¥)</span>
        <input inputMode="decimal" value={cotacaoKit} onChange={(e) => setCotacaoKit(e.target.value)} />
        <small>
          O acréscimo usado quando a peça faz parte de um kit, no lugar do acréscimo da faixa. É aqui que vive o
          desconto do kit: menor que o normal, a mesma faca sai mais barata levando a luva junto. Como as faixas
          variam, o desconto em % muda de um kit para outro — a vitrine calcula e mostra.
        </small>
      </label>

      <div className="table-wrap">

        <table className="admin-table">
        <thead>
          <tr>
            <th>De (R$)</th>
            <th>Até (R$)</th>
            <th>Acréscimo na cotação (R$ por ¥)</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rascunho.map((t, i) => (
            <tr key={i} className="tier-row">
              <td>
                <input inputMode="decimal" value={t.minBrl} onChange={(e) => editar(i, 'minBrl', e.target.value)} />
              </td>
              <td>
                <input
                  inputMode="decimal"
                  value={t.maxBrl}
                  placeholder="sem limite"
                  onChange={(e) => editar(i, 'maxBrl', e.target.value)}
                />
              </td>
              <td>
                <input inputMode="decimal" value={t.rateBonus} onChange={(e) => editar(i, 'rateBonus', e.target.value)} />
              </td>
              <td>
                <button className="btn ghost small" onClick={() => remover(i)}>
                  Remover
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
          </div>

      <div className="actions-row">
        <button className="btn ghost" onClick={adicionar}>
          + Faixa
        </button>
        <button className="btn primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar tabela'}
        </button>
      </div>

      <h3>Simulação</h3>
      <p className="muted">
        A conta inteira, etapa por etapa. Confira antes de salvar: margem em porcentagem sobre faixa engana a conta
        de cabeça. Os valores refletem a tabela <b>já salva</b>.
      </p>
      <div className="table-wrap">
        <table className="admin-table">
        <thead>
          <tr>
            <th>Preço ¥</th>
            <th>Com taxa ¥</th>
            <th>Custo R$</th>
            <th>Cotação usada</th>
            <th>Preço ao cliente</th>
            <th>Lucro</th>
          </tr>
        </thead>
        <tbody>
          {data.simulacao.map((s) => (
            <tr key={s.custoCny}>
              <td>¥ {s.custoCny.toLocaleString('pt-BR')}</td>
              <td className="dim">¥ {s.comTaxaCny.toLocaleString('pt-BR')}</td>
              <td>{brl(s.custoBrl)}</td>
              <td className="dim">
                {s.cotacaoVenda.toFixed(4)} <small>(+{s.rateBonus.toFixed(4)})</small>
              </td>
              <td>
                <b>{brl(s.vendaBrl)}</b>
              </td>
              <td className="pos">{brl(s.lucroBrl)}</td>
            </tr>
          ))}
        </tbody>
      </table>
          </div>

      {preview && (
        <>
          <h3>Catálogo com as duas pontas</h3>
          <p className="muted">
            O que o marketplace cobra e o que o site mostra — do mesmo desgaste, o mais barato que o cliente
            enxerga. Esta é a única tela em que o custo aparece.
          </p>
          <div className="table-wrap">
            <table className="admin-table">
            <thead>
              <tr>
                <th>Skin</th>
                <th>Custo</th>
                <th>Acréscimo</th>
                <th>Preço ao cliente</th>
                <th>Lucro</th>
              </tr>
            </thead>
            <tbody>
              {preview.items.map((i) => (
                <tr key={i.key}>
                  <td>{i.name}</td>
                  <td>{brl(i.custoBrl)}</td>
                  <td className="dim">{i.rateBonus === null ? '—' : `+${i.rateBonus.toFixed(4)}`}</td>
                  <td>
                    <b>{brl(i.vendaBrl)}</b>
                  </td>
                  <td className="pos">{brl(i.lucroBrl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Clientes ────────────────────────────────────────────────────────────────

function PainelClientes() {
  const [data, setData] = useState<AdminClientsResponse | null>(null);
  const [busca, setBusca] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setData(await api.adminClients({ search: busca || undefined, flagged: flagged || undefined, limit: 100 }));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os clientes.');
    }
  }, [busca, flagged]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function alternarBloqueio(c: AdminClient) {
    const bloquear = c.status === 'active';
    const motivo = bloquear ? window.prompt('Motivo do bloqueio (o cliente vê esta mensagem):') ?? undefined : undefined;
    if (bloquear && motivo === undefined) return;
    await api.adminSetClientStatus(c.id, bloquear ? 'blocked' : 'active', motivo);
    await carregar();
  }

  async function resolverNome(c: AdminClient) {
    if (!window.confirm(`Confirmar que "${c.personaName}" é a mesma pessoa e limpar o alerta?`)) return;
    await api.adminAckNameChange(c.id);
    await carregar();
  }

  if (erro) return <Banner kind="error">{erro}</Banner>;
  if (!data) return <Spinner label="Carregando clientes…" />;

  return (
    <div className="panel">
      <h2>Clientes</h2>
      <div className="stat-row">
        <span>
          <b>{data.totals.total}</b> contas
        </span>
        <span>
          <b>{data.totals.with_trade_url}</b> com trade link
        </span>
        <span className={data.totals.name_changed ? 'warn' : ''}>
          <b>{data.totals.name_changed}</b> trocaram o nome
        </span>
        <span>
          <b>{data.totals.blocked}</b> bloqueadas
        </span>
      </div>

      <div className="actions-row">
        <input
          placeholder="Buscar por SteamID, apelido, WhatsApp…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <label className="check inline">
          <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} />
          <span>Só quem trocou o nome</span>
        </label>
      </div>

      <div className="table-wrap">

        <table className="admin-table">
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Contato</th>
            <th>Trade link</th>
            <th>Cadastro</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.items.map((c) => (
            <tr key={c.id} className={c.status === 'blocked' ? 'row-off' : ''}>
              <td>
                <div className="cli">
                  {c.avatarUrl && <img src={c.avatarUrl} alt="" />}
                  <div>
                    <b>{c.personaName ?? '—'}</b>
                    <div className="dim">{c.steamId}</div>
                    {c.nameChanged && (
                      <div className="warn">
                        ⚠ trocou o nome — cadastrou como <b>{c.nameAtTradeUrl}</b>
                      </div>
                    )}
                    {c.status === 'blocked' && <div className="neg">bloqueado: {c.blockedReason ?? 'sem motivo'}</div>}
                  </div>
                </div>
              </td>
              <td>{c.whatsapp ?? '—'}</td>
              <td className="break">
                {c.tradeUrl ? (
                  <a href={c.tradeUrl} target="_blank" rel="noreferrer">
                    abrir
                  </a>
                ) : (
                  <span className="dim">não informado</span>
                )}
              </td>
              <td className="dim">{dateTime(c.createdAt)}</td>
              <td>
                <div className="actions">
                  {c.nameChanged && (
                    <button className="btn ghost small" onClick={() => resolverNome(c)}>
                      Resolver
                    </button>
                  )}
                  <button className="btn ghost small" onClick={() => alternarBloqueio(c)}>
                    {c.status === 'active' ? 'Bloquear' : 'Liberar'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
          </div>
      {data.items.length === 0 && <p className="muted">Nenhum cliente com esse filtro.</p>}
    </div>
  );
}

// ─── Catálogo ────────────────────────────────────────────────────────────────

/** 90 → "1h 30min"; 45 → "45min". */
function formatarIntervalo(minutos: number): string {
  if (minutos <= 0) return 'agora';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return [h > 0 ? `${h}h` : '', m > 0 ? `${m}min` : ''].filter(Boolean).join(' ');
}

function PainelCatalogo() {
  const [sync, setSync] = useState<SyncProgress | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const { currency } = useCurrency();

  const carregar = useCallback(async () => {
    try {
      setSync(await api.syncStatus());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui ler o status.');
    }
  }, []);

  useEffect(() => {
    void carregar();
    const timer = window.setInterval(() => void carregar(), 5000);
    return () => window.clearInterval(timer);
  }, [carregar]);

  return (
    <div className="panel">
      <h2>Catálogo e sincronização</h2>
      {erro && <Banner kind="error">{erro}</Banner>}
      {sync && (
        <>
          <div className="stat-row">
            <span>
              <b>{sync.catalogSize ?? 0}</b> itens no catálogo
            </span>
            <span>
              estado: <b>{sync.running ? 'rodando' : 'parado'}</b>
            </span>
            <span>
              página <b>{sync.page}</b>
              {sync.totalPages ? ` / ${sync.totalPages}` : ''}
            </span>
            <span>
              <b>{sync.newItems}</b> novos nesta rodada
            </span>
            <span className="dim">moeda de exibição: {currency}</span>
          </div>

          {sync.scheduler && (
            <div className="stat-row">
              <span>
                automática:{' '}
                <b>
                  {sync.scheduler.ativo
                    ? `a cada ${formatarIntervalo(sync.scheduler.intervaloMinutos)}`
                    : 'desligada'}
                </b>
              </span>
              {sync.scheduler.ativo && sync.scheduler.faltamMinutos !== null && (
                <span>
                  próxima em <b>{formatarIntervalo(sync.scheduler.faltamMinutos)}</b>
                </span>
              )}
              {sync.scheduler.ultimaTentativa && (
                <span className="dim">
                  última tentativa: {sync.scheduler.ultimaTentativa.resultado} (
                  {dateTime(sync.scheduler.ultimaTentativa.em)})
                </span>
              )}
            </div>
          )}
          <div className="actions-row">
            <button className="btn primary" onClick={() => void api.startSync().then(setSync)} disabled={sync.running}>
              Rodar sincronização completa
            </button>
            <button className="btn ghost" onClick={() => void api.cancelSync().then(setSync)} disabled={!sync.running}>
              Cancelar
            </button>
            <button className="btn ghost" onClick={() => void api.harvestCatalog(6).then(() => carregar())}>
              Coletar vitrine (6 rodadas)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

